/**
 * Date Utilities for Supply Round Up
 *
 * Business Days = Excludes weekends (Sat/Sun) and US Federal Holidays
 * Calendar Days = All days including weekends and holidays
 */

/**
 * Parse a date string as a local date (not UTC) to avoid timezone shift issues.
 * JavaScript's `new Date("2026-03-30")` parses as UTC midnight, which can shift
 * to the previous day in local time. This function parses as local midnight.
 *
 * @param {string|Date} dateInput - Date string (YYYY-MM-DD) or Date object
 * @returns {Date|null} - Local Date object or null if invalid
 */
export const parseLocalDate = (dateInput) => {
  if (!dateInput) return null;
  if (dateInput instanceof Date) return new Date(dateInput);

  // Parse YYYY-MM-DD string as local date
  if (typeof dateInput === 'string') {
    const parts = dateInput.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // 0-indexed
      const day = parseInt(parts[2], 10);
      const date = new Date(year, month, day);
      if (!isNaN(date.getTime())) return date;
    }
    // Fallback for other formats
    const fallback = new Date(dateInput);
    return isNaN(fallback.getTime()) ? null : fallback;
  }

  return null;
};

// US Federal Holidays (fixed dates + calculated dates)
// Returns holidays for a given year
// Cache holidays by year for performance
const holidayCache = new Map();

export const getUSHolidays = (year) => {
  // Return cached holidays if available
  if (holidayCache.has(year)) {
    return holidayCache.get(year);
  }

  const holidays = [];
  
  // Fixed date holidays
  holidays.push(new Date(year, 0, 1));   // New Year's Day - Jan 1
  holidays.push(new Date(year, 6, 4));   // Independence Day - Jul 4
  holidays.push(new Date(year, 10, 11)); // Veterans Day - Nov 11
  holidays.push(new Date(year, 11, 25)); // Christmas Day - Dec 25
  
  // Calculated holidays
  
  // MLK Day - 3rd Monday of January
  holidays.push(getNthWeekdayOfMonth(year, 0, 1, 3));
  
  // Presidents Day - 3rd Monday of February
  holidays.push(getNthWeekdayOfMonth(year, 1, 1, 3));
  
  // Memorial Day - Last Monday of May
  holidays.push(getLastWeekdayOfMonth(year, 4, 1));
  
  // Labor Day - 1st Monday of September
  holidays.push(getNthWeekdayOfMonth(year, 8, 1, 1));
  
  // Columbus Day - 2nd Monday of October
  holidays.push(getNthWeekdayOfMonth(year, 9, 1, 2));
  
  // Thanksgiving - 4th Thursday of November
  holidays.push(getNthWeekdayOfMonth(year, 10, 4, 4));
  
  // Day after Thanksgiving (common company holiday)
  const thanksgiving = getNthWeekdayOfMonth(year, 10, 4, 4);
  holidays.push(new Date(thanksgiving.getTime() + 24 * 60 * 60 * 1000));

  // Cache the result
  holidayCache.set(year, holidays);
  return holidays;
};

// Get nth weekday of a month (e.g., 3rd Monday)
// weekday: 0=Sun, 1=Mon, ..., 6=Sat
const getNthWeekdayOfMonth = (year, month, weekday, n) => {
  const firstDay = new Date(year, month, 1);
  const firstWeekday = firstDay.getDay();
  let dayOffset = weekday - firstWeekday;
  if (dayOffset < 0) dayOffset += 7;
  const day = 1 + dayOffset + (n - 1) * 7;
  return new Date(year, month, day);
};

// Get last weekday of a month (e.g., last Monday of May)
const getLastWeekdayOfMonth = (year, month, weekday) => {
  const lastDay = new Date(year, month + 1, 0);
  const lastWeekday = lastDay.getDay();
  let dayOffset = lastWeekday - weekday;
  if (dayOffset < 0) dayOffset += 7;
  return new Date(year, month + 1, -dayOffset);
};

// Cache holiday date strings by year for O(1) lookup
const holidaySetCache = new Map();

const getHolidaySet = (year) => {
  if (holidaySetCache.has(year)) {
    return holidaySetCache.get(year);
  }
  const holidays = getUSHolidays(year);
  const holidaySet = new Set(holidays.map(h => `${h.getMonth()}-${h.getDate()}`));
  holidaySetCache.set(year, holidaySet);
  return holidaySet;
};

// Check if a date is a US holiday (O(1) lookup)
export const isUSHoliday = (date) => {
  const year = date.getFullYear();
  const holidaySet = getHolidaySet(year);
  return holidaySet.has(`${date.getMonth()}-${date.getDate()}`);
};

// Check if a date is a weekend
export const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

// Check if a date is a business day
export const isBusinessDay = (date) => {
  return !isWeekend(date) && !isUSHoliday(date);
};

/**
 * Add business days to a date
 * @param {Date|string} startDate - Starting date
 * @param {number} days - Number of business days to add (can be negative)
 * @returns {Date} - Resulting date
 */
export const addBusinessDays = (startDate, days) => {
  const result = parseLocalDate(startDate) || new Date(startDate);
  const direction = days >= 0 ? 1 : -1;
  let remaining = Math.abs(days);
  
  while (remaining > 0) {
    result.setDate(result.getDate() + direction);
    if (isBusinessDay(result)) {
      remaining--;
    }
  }
  
  return result;
};

/**
 * Subtract business days from a date
 * @param {Date} startDate - Starting date
 * @param {number} days - Number of business days to subtract
 * @returns {Date} - Resulting date
 */
export const subtractBusinessDays = (startDate, days) => {
  return addBusinessDays(startDate, -days);
};

/**
 * Get the number of business days between two dates
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {number} - Number of business days (negative if endDate < startDate)
 */
export const getBusinessDaysDiff = (startDate, endDate) => {
  const start = parseLocalDate(startDate) || new Date(startDate);
  const end = parseLocalDate(endDate) || new Date(endDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  
  if (start.getTime() === end.getTime()) return 0;
  
  const isNegative = end < start;
  const [earlier, later] = isNegative ? [end, start] : [start, end];
  
  let count = 0;
  const current = new Date(earlier);
  
  while (current < later) {
    current.setDate(current.getDate() + 1);
    if (isBusinessDay(current)) {
      count++;
    }
  }
  
  return isNegative ? -count : count;
};

/**
 * Get the number of calendar days between two dates
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {number} - Number of calendar days (negative if endDate < startDate)
 */
export const getCalendarDaysDiff = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  
  const diffTime = end.getTime() - start.getTime();
  return Math.round(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Configuration defaults (can be overridden by user settings)
 */
export const DEFAULT_CONFIG = {
  atRiskBuffer: 5,                        // legacy; kept for stored-settings compatibility
  retailWholesaleBuffer: 16,              // Days before launch product must be in DC (Retail/Wholesale)
  retailWholesaleBufferUnit: 'calendar',  // 'calendar' | 'business'
  ecommBuffer: 2,                         // Days before launch product must be in DC (Ecomm/DTC)
  ecommBufferUnit: 'business',            // 'calendar' | 'business'
};

/**
 * Add days to a date honoring the configured unit.
 * @param {Date|string} startDate
 * @param {number} days - can be negative
 * @param {'business'|'calendar'} unit
 */
export const addBufferDays = (startDate, days, unit = 'business') => {
  if (unit === 'calendar') {
    const d = parseLocalDate(startDate) || new Date(startDate);
    d.setDate(d.getDate() + days);
    return d;
  }
  return addBusinessDays(startDate, days);
};

export const subtractBufferDays = (startDate, days, unit = 'business') => addBufferDays(startDate, -days, unit);

/**
 * Resolve the DC-arrival buffer for a channel from config.
 * @returns {{days: number, unit: 'business'|'calendar'}}
 */
export const getChannelBuffer = (channel, config = {}) => {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const ch = (channel || '').toLowerCase();
  const isRetailWholesale = ch.includes('retail') || ch.includes('wholesale') || ch.includes('whsl') || ch === 'r' || ch === 'w';
  return isRetailWholesale
    ? { days: cfg.retailWholesaleBuffer, unit: cfg.retailWholesaleBufferUnit || 'business' }
    : { days: cfg.ecommBuffer, unit: cfg.ecommBufferUnit || 'business' };
};

/**
 * Calculate the effective launch date for a PO based on Launch Status and Channel.
 *
 * Logic:
 * - Carryover PO / Blank: Est In DC + lead time buffer
 * - Launch PO: Channel-specific launch date (fallback to ecomm launch)
 * - Relaunch PO: Channel-specific relaunch date (fallback chain to ecomm)
 *
 * @param {Object} po - Purchase order object with date fields and channel
 * @param {Object} config - Optional config overrides
 * @returns {Date|null} - The effective launch date, or null if cannot be determined
 */
export const getEffectiveLaunchDate = (po, config = {}) => {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const launchStatus = po.launchStatus || '';
  const channel = (po.channel || '').toLowerCase();
  const estInDc = parseLocalDate(po.projInDCDate);

  // Ecomm launch date (base fallback)
  const ecommLaunch = po.originalLaunchDate || po.launchDate;

  // ---------------------------------------------------------
  // Carryover PO or Blank: Use Est In DC + lead time buffer
  // ---------------------------------------------------------
  if (launchStatus === 'Carryover PO' || launchStatus === '') {
    if (estInDc && !isNaN(estInDc.getTime())) {
      const { days, unit } = getChannelBuffer(channel, cfg);
      return addBufferDays(estInDc, days, unit);
    }
    // Carryover with no Est In DC: no meaningful deadline (product already launched)
    return null;
  }

  // ---------------------------------------------------------
  // Launch PO: Use channel-specific launch date
  // ---------------------------------------------------------
  if (launchStatus === 'Launch PO') {
    if (channel.includes('retail')) {
      // Retail: item_retail_launch_date → launch_date
      return parseLocalDate(po.itemRetailLaunchDate) || parseLocalDate(ecommLaunch);
    }
    if (channel.includes('wholesale')) {
      // Wholesale: item_whsl_launch_date → launch_date
      return parseLocalDate(po.itemWhslLaunchDate) || parseLocalDate(ecommLaunch);
    }
    // Ecomm: launch_date
    return parseLocalDate(ecommLaunch);
  }

  // ---------------------------------------------------------
  // Relaunch PO: Use channel-specific relaunch date
  // ---------------------------------------------------------
  if (launchStatus === 'Relaunch PO') {
    if (channel.includes('retail')) {
      // Retail: item_retail_relaunch_date → item_relaunch_date → launch_date
      return parseLocalDate(po.itemRetailRelaunchDate)
          || parseLocalDate(po.itemRelaunchDate)
          || parseLocalDate(ecommLaunch);
    }
    if (channel.includes('wholesale')) {
      // Wholesale: item_relaunch_date → launch_date (no whsl relaunch field)
      return parseLocalDate(po.itemRelaunchDate) || parseLocalDate(ecommLaunch);
    }
    // Ecomm: item_relaunch_date → launch_date
    return parseLocalDate(po.itemRelaunchDate) || parseLocalDate(ecommLaunch);
  }

  // ---------------------------------------------------------
  // Unknown status: fallback to ecomm launch
  // ---------------------------------------------------------
  return parseLocalDate(ecommLaunch);
};

/**
 * Format a Date object as YYYY-MM-DD string
 * @param {Date} date - Date object to format
 * @returns {string|null} - Formatted date string or null
 */
export const formatDateString = (date) => {
  if (!date || isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

/**
 * Calculate Allocation Need Date based on launch date and channel
 * Uses the existing lead time settings (retailWholesaleBuffer, ecommBuffer)
 *
 * @param {string|Date} launchDate - The launch date
 * @param {string} channel - The channel (Ecomm, Retail, Wholesale, etc.)
 * @param {Object} config - Optional config overrides
 * @returns {Date|null} - The allocation need date, or null if no launch date
 */
export const getAllocationNeedDate = (launchDate, channel, config = {}) => {
  if (!launchDate) return null;

  const cfg = { ...DEFAULT_CONFIG, ...config };
  const launch = parseLocalDate(launchDate);
  if (!launch) return null;

  const { days, unit } = getChannelBuffer(channel, cfg);
  return subtractBufferDays(launch, days, unit);
};

/**
 * Determine PO status based on dates and shipment info
 * 
 * @param {Object} po - Purchase order object
 * @param {Object} config - Optional config overrides
 * @returns {Object} - { status, statusLabel, statusColor, daysUntilDeadline, mustArriveBy, subStatus }
 */
/**
 * THE single arrival-risk classifier (Nate's ruling 2026-07-30): calendar
 * comparison against hard dates. A launch date is a hard date — goods landing
 * ANY day after it are late, weekends included (ecomm sells on Saturday).
 * Working-day reality lives in the channel alloc buffers (ecomm 2 biz days,
 * retail/whsl 16 calendar), NOT in the verdict comparison.
 * Both getPOStatus (modal rows, Trail Notes verbatim statuses) and
 * getGroupRisk (tile dots) MUST call this — never rebuild the comparison.
 *
 * @param {Date|null} estDcDate    parsed est-in-DC
 * @param {Date|null} launchDate   parsed effective launch
 * @param {Date|null} allocNeedDate parsed allocation need (launch − channel buffer)
 * @returns {'late'|'atRisk'|'onTrack'}
 */
export const classifyArrivalRisk = (estDcDate, launchDate, allocNeedDate) => {
  if (estDcDate && launchDate && estDcDate > launchDate) return 'late';
  if (estDcDate && allocNeedDate && estDcDate > allocNeedDate) return 'atRisk';
  return 'onTrack';
};

export const getPOStatus = (po, config = {}) => {
  const cfg = { ...DEFAULT_CONFIG, ...config };

  // Get quantities - support both field naming conventions
  const openQty = po.openQty ?? (po.poQty - (po.receivedQty || po.poQtyReceived || 0));
  const receivedQty = po.receivedQty || po.poQtyReceived || 0;

  // Check if fully received (no open quantity)
  const isFullyReceived = openQty <= 0;
  if (isFullyReceived) {
    return {
      status: 'received',
      statusLabel: 'Received',
      statusColor: '#6B7F5E', // olive
      daysUntilDeadline: null,
      mustArriveBy: null,
      subStatus: null
    };
  }

  // Check if has any receipts
  const hasPartialReceipts = receivedQty > 0;

  // Check if in transit (has shipment number)
  const isInTransit = po.shipmentNumber && po.shipmentNumber.length > 0;

  // Get Est. DC date (use parseLocalDate to avoid timezone shift)
  const estDcDate = parseLocalDate(po.projInDCDate);
  if (estDcDate) estDcDate.setHours(0, 0, 0, 0);

  // Get effective launch date using centralized logic
  const channel = (po.channel || '').toLowerCase();
  let effectiveLaunchDate = getEffectiveLaunchDate(po, cfg);
  if (effectiveLaunchDate) effectiveLaunchDate.setHours(0, 0, 0, 0);

  // Calculate allocation need date using effective launch
  const allocNeedDate = effectiveLaunchDate ? getAllocationNeedDate(effectiveLaunchDate, channel, cfg) : null;

  // Verdicts come from the single shared classifier (calendar vs hard dates —
  // see classifyArrivalRisk). Buffers are already channel-aware in allocNeedDate.
  // Carryover (and blank status) has NO hard deadline — its "effective launch"
  // is est-in-DC + buffer, a derived date, so late/at-risk verdicts are
  // meaningless (getGroupRisk has the same exemption; the add/sub business-day
  // round-trip otherwise fabricates At Risk on weekend est-in-DC dates).
  const poLaunchStatus = po.launchStatus || '';
  const hasHardDeadline = poLaunchStatus === 'Launch PO' || poLaunchStatus === 'Relaunch PO';
  const arrivalRisk = hasHardDeadline
    ? classifyArrivalRisk(estDcDate, effectiveLaunchDate, allocNeedDate)
    : 'onTrack';
  if (arrivalRisk === 'late') {
    return {
      status: 'late',
      statusLabel: isInTransit ? 'In Transit / Late' : 'Open / Late',
      statusColor: '#A85D4A', // rust
      daysUntilDeadline: null,
      mustArriveBy: effectiveLaunchDate,
      subStatus: isInTransit ? 'late_in_transit' : 'late_at_factory'
    };
  }

  // AT RISK: past the allocation need date (but not past launch)
  if (arrivalRisk === 'atRisk') {
    return {
      status: 'atRisk',
      statusLabel: isInTransit ? 'In Transit / At Risk' : 'Open / At Risk',
      statusColor: '#D97706', // amber
      daysUntilDeadline: null,
      mustArriveBy: allocNeedDate,
      subStatus: null
    };
  }

  // IN TRANSIT: Has shipment number (on time)
  if (isInTransit) {
    return {
      status: 'inTransit',
      statusLabel: hasPartialReceipts ? 'In Transit (Partial)' : 'In Transit',
      statusColor: '#6B7F5E', // olive
      daysUntilDeadline: null,
      mustArriveBy: allocNeedDate,
      subStatus: hasPartialReceipts ? 'partial_receipt' : null
    };
  }

  // OPEN: No shipment number (not at risk or late)
  return {
    status: 'open',
    statusLabel: 'Open',
    statusColor: '#2D4F3F', // forest
    daysUntilDeadline: null,
    mustArriveBy: allocNeedDate,
    subStatus: null
  };
};

/**
 * Format days for display
 * Shows business days with calendar days in parentheses if different
 * 
 * @param {number} businessDays - Number of business days
 * @param {Date} fromDate - Starting date for calendar calculation
 * @param {Date} toDate - Ending date for calendar calculation
 * @returns {string} - Formatted string like "-3" or "+5 (7 cal)"
 */
export const formatDaysDisplay = (businessDays, fromDate, toDate) => {
  if (businessDays === null || businessDays === undefined) return '—';
  
  const prefix = businessDays >= 0 ? '+' : '';
  const calendarDays = fromDate && toDate ? getCalendarDaysDiff(fromDate, toDate) : null;
  
  // If calendar days differ significantly, show both
  if (calendarDays !== null && Math.abs(calendarDays - businessDays) >= 2) {
    return `${prefix}${businessDays} (${calendarDays} cal)`;
  }
  
  return `${prefix}${businessDays}`;
};

/**
 * THE single adapter from a purchase-order row (any shape: app camelCase,
 * RPC pos[] JSON, or raw snake_case DB columns) to the canonical input for
 * getEffectiveLaunchDate / getAllocationNeedDate. Every page must go through
 * this — hand-built adapters are how the four-way drift happened.
 */
export const poToDateInputs = (po) => ({
  launchStatus: po.launchStatus ?? po.launch_status ?? '',
  launchDate: po.launchDate ?? po.launch_date ?? null,
  originalLaunchDate: po.originalLaunchDate ?? po.launchDate ?? po.launch_date ?? null,
  projInDCDate: po.projInDCDate ?? po.estInDcDate ?? po.est_in_dc_date ?? null,
  itemRelaunchDate: po.itemRelaunchDate ?? po.item_relaunch_date ?? null,
  itemRetailRelaunchDate: po.itemRetailRelaunchDate ?? po.item_retail_relaunch_date ?? null,
  itemRetailLaunchDate: po.itemRetailLaunchDate ?? po.item_retail_launch_date ?? null,
  itemWhslLaunchDate: po.itemWhslLaunchDate ?? po.item_whsl_launch_date ?? null,
  channel: String(po.channel ?? po.virtual_inventory_channel ?? '').replace('FG - ', ''),
});

/**
 * Earliest effective launch + allocation need date across a set of POs.
 * Launch/Relaunch POs only — a carryover's alloc is definitionally its
 * est-in-DC (no launch deadline), so it never drives these rollups.
 */
export const getEarliestLaunchDates = (pos, config = {}) => {
  let earliestLaunchDate = null;
  let earliestAllocDate = null;
  for (const po of pos || []) {
    const input = poToDateInputs(po);
    if (input.launchStatus !== 'Launch PO' && input.launchStatus !== 'Relaunch PO') continue;
    const launch = getEffectiveLaunchDate(input, config);
    if (!launch) continue;
    if (!earliestLaunchDate || launch < earliestLaunchDate) earliestLaunchDate = launch;
    const alloc = getAllocationNeedDate(launch, input.channel, config);
    if (alloc && (!earliestAllocDate || alloc < earliestAllocDate)) earliestAllocDate = alloc;
  }
  return { earliestLaunchDate, earliestAllocDate };
};

/**
 * THE single risk assessment for a Launch Line Up RPC group row.
 * Used by the card border, the E/R/W channel dots, the stats bar, and the
 * Round Up export — one implementation, everywhere.
 *
 * Model: risk applies to Launch/Relaunch groups only. A channel is
 *   LATE     when est-in-DC lands after that channel's launch date
 *   AT RISK  when est-in-DC lands after (launch − channel buffer)
 * Carryover has no launch deadline (effective launch is DEFINED as
 * est-in-DC + buffer, so it can never miss itself) → always on track.
 *
 * @param {Object} row - RPC row: est_in_dc_date, open_qty, status_group,
 *   {ecomm|retail|whsl}_open_qty, {ecomm|retail|whsl}_launch_date, effective_launch_date
 * @param {Object} config - calc settings
 * @param {boolean} overridden - a range report mitigates this CC's risk
 * @returns {{worstStatus: string, channelStatus: {E,R,W}, mitigated: boolean}}
 */
export const getGroupRisk = (row, config = {}, overridden = false) => {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  if ((row.open_qty || 0) <= 0) {
    return { worstStatus: 'received', channelStatus: { E: null, R: null, W: null }, mitigated: false };
  }

  const channelStatus = { E: null, R: null, W: null };
  let worstStatus = 'open';

  if (row.status_group !== 'carryover') {
    const estDC = parseLocalDate(row.est_in_dc_date);
    const priority = { late: 3, atRisk: 2, onTrack: 1 };
    const channels = [
      { key: 'E', qty: row.ecomm_open_qty, launch: row.ecomm_launch_date, name: 'ecomm' },
      { key: 'R', qty: row.retail_open_qty, launch: row.retail_launch_date, name: 'retail' },
      { key: 'W', qty: row.whsl_open_qty, launch: row.whsl_launch_date, name: 'wholesale' },
    ];
    for (const c of channels) {
      if (!c.qty || c.qty <= 0) continue;
      const launch = parseLocalDate(c.launch || row.effective_launch_date);
      let s = 'onTrack';
      if (estDC && launch) {
        const { days, unit } = getChannelBuffer(c.name, cfg);
        const allocNeed = subtractBufferDays(launch, days, unit);
        // Same shared classifier the modal rows use — never rebuilt inline.
        s = classifyArrivalRisk(estDC, launch, allocNeed);
      }
      channelStatus[c.key] = s;
      const worstKey = worstStatus === 'open' ? 'onTrack' : worstStatus;
      if (priority[s] > (priority[worstKey] || 0)) worstStatus = s === 'onTrack' ? 'open' : s;
    }
  } else {
    // carryover: mark active channels on-track so dots still render
    if (row.ecomm_open_qty > 0) channelStatus.E = 'onTrack';
    if (row.retail_open_qty > 0) channelStatus.R = 'onTrack';
    if (row.whsl_open_qty > 0) channelStatus.W = 'onTrack';
  }

  if (overridden && (worstStatus === 'late' || worstStatus === 'atRisk')) {
    return { worstStatus: 'mitigated', channelStatus, mitigated: true };
  }
  return { worstStatus, channelStatus, mitigated: false };
};

// =============================================================================
// Central Time helpers
// =============================================================================
// Tecovas operates on Central Time. Storage is TIMESTAMPTZ (UTC under the hood)
// but every user-facing time MUST render in America/Chicago. Route every
// display/comparison through these helpers — never reach for `toLocaleString()`
// without an explicit timeZone option.

const CT_ZONE = 'America/Chicago';

/**
 * Format a TIMESTAMPTZ string / Date in Central Time.
 *
 * @param {string|Date|null|undefined} value - ISO string, Date, or nullish
 * @param {object} [options]
 * @param {'date'|'time'|'datetime'|'short'} [options.style='datetime']
 *   - 'date'     → "5/6/2026"
 *   - 'time'     → "9:30 AM"
 *   - 'datetime' → "5/6/2026, 9:30 AM"
 *   - 'short'    → "5/6 9:30 AM"
 * @param {string} [options.fallback='—'] - what to render when value is nullish
 * @returns {string}
 */
export const formatCT = (value, { style = 'datetime', fallback = '—' } = {}) => {
  if (!value) return fallback;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return fallback;

  const opts = { timeZone: CT_ZONE };
  if (style === 'date') {
    Object.assign(opts, { year: 'numeric', month: 'numeric', day: 'numeric' });
  } else if (style === 'time') {
    Object.assign(opts, { hour: 'numeric', minute: '2-digit' });
  } else if (style === 'short') {
    Object.assign(opts, { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } else {
    Object.assign(opts, { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }
  return new Intl.DateTimeFormat('en-US', opts).format(d);
};

/**
 * Get the YYYY-MM-DD calendar day in Central Time for a TIMESTAMPTZ.
 * Useful for grouping by day, comparing with HTML date inputs, etc.
 *
 * @param {string|Date} value
 * @returns {string|null} - "2026-05-06" or null on invalid input
 */
export const ctDayString = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;
  // en-CA gives ISO-style YYYY-MM-DD format
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CT_ZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);
};

/**
 * Today's date in Central Time as YYYY-MM-DD.
 */
export const todayCT = () => ctDayString(new Date());

/**
 * Resolve any date-ish value (bare 'YYYY-MM-DD' string OR timestamp) to a CT
 * calendar day string. Bare dates pass through untouched — running them through
 * new Date() would parse as UTC midnight and render as the PREVIOUS day in CT.
 * Use this before any day-level comparison of ETA/date fields.
 */
export const toCtDay = (value) => {
  if (!value) return null;
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return ctDayString(s);
};

/**
 * Return the CT (America/Chicago) UTC offset in hours for a calendar date.
 * Returns 5 (CDT) or 6 (CST). DST-aware via Intl.
 *
 * @param {number} year - full year e.g. 2026
 * @param {number} month - 1-12
 * @param {number} day - 1-31
 * @returns {number}
 */
export const ctOffsetHoursForDate = (year, month, day) => {
  const probe = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  const ctHour = parseInt(
    probe.toLocaleString('en-US', {
      timeZone: CT_ZONE,
      hour: 'numeric',
      hour12: false,
    }),
    10,
  );
  return 12 - ctHour;
};

/**
 * Convert CT wall-clock components to a UTC ms epoch. DST-aware.
 * Use this when something gives you a wall-clock that is documented as CT
 * (filename stamps, file payloads, SLA deadlines, etc.) — never hard-code
 * `-05:00` because it breaks every November.
 *
 * @param {number} year - 1-9999
 * @param {number} month - 1-12
 * @param {number} day - 1-31
 * @param {number} hour - 0-23
 * @param {number} [minute=0] - 0-59
 * @param {number} [second=0] - 0-59
 * @returns {number} UTC ms epoch
 */
export const ctWallClockToUtcMs = (year, month, day, hour, minute = 0, second = 0) => {
  const offset = ctOffsetHoursForDate(year, month, day);
  return Date.UTC(year, month - 1, day, hour + offset, minute, second);
};
